import { Component } from '@theme/component';

/**
 * @typedef {Object} ElifeSliderRefs
 * @property {HTMLElement} track - Contenedor con scroll horizontal (scroll-snap)
 * @property {HTMLElement[]} slides - Elementos del carrusel
 * @property {HTMLButtonElement} [previous] - Botón anterior
 * @property {HTMLButtonElement} [next] - Botón siguiente
 * @property {HTMLButtonElement[]} [dots] - Puntos de navegación (uno por slide)
 */

/**
 * Carrusel Elife sobre scroll-snap nativo: sin JS se navega con scroll/swipe.
 * Con JS agrega flechas, puntos, estado de los botones en los extremos y autoplay opcional
 * (`data-autoplay` en segundos, pausa con hover/foco, nunca con prefers-reduced-motion).
 * `data-loop` hace que las flechas y el autoplay vuelvan al inicio al llegar al final.
 *
 * @extends {Component<ElifeSliderRefs>}
 */
class ElifeSlider extends Component {
  requiredRefs = ['track', 'slides'];

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {number | null} */
  #timer = null;

  #paused = false;

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.refs.track.addEventListener('scroll', this.#handleScroll, { signal, passive: true });
    this.addEventListener('pointerenter', this.#pause, { signal });
    this.addEventListener('pointerleave', this.#resume, { signal });
    this.addEventListener('focusin', this.#pause, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });
    this.#reducedMotion.addEventListener('change', this.#restartAutoplay, { signal });

    this.dataset.enhanced = '';
    this.#updateControls();
    this.#restartAutoplay();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#updateControls();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
    this.#stopAutoplay();
  }

  next() {
    this.#step(1);
    this.#restartAutoplay();
  }

  previous() {
    this.#step(-1);
    this.#restartAutoplay();
  }

  /**
   * Va al slide del punto presionado.
   * @param {MouseEvent} event
   */
  goTo(event) {
    const dot = event.target instanceof Element ? event.target.closest('[data-index]') : null;
    if (!(dot instanceof HTMLElement)) return;

    this.#scrollToSlide(Number(dot.dataset.index));
    this.#restartAutoplay();
  }

  get #isRtl() {
    return getComputedStyle(this).direction === 'rtl';
  }

  get #behavior() {
    return this.#reducedMotion.matches ? 'auto' : 'smooth';
  }

  /** Índice del primer slide visible */
  get #currentIndex() {
    const { track, slides } = this.refs;
    const trackStart = track.getBoundingClientRect().left;
    let closest = 0;
    let smallest = Infinity;

    slides.forEach((slide, index) => {
      const rect = slide.getBoundingClientRect();
      const start = this.#isRtl ? trackStart + track.clientWidth - rect.right : rect.left - trackStart;
      const distance = Math.abs(start);
      if (distance < smallest) {
        smallest = distance;
        closest = index;
      }
    });

    return closest;
  }

  /** Cuántos slides caben en el ancho visible (mínimo 1) */
  get #perPage() {
    const [first] = this.refs.slides;
    if (!first) return 1;
    return Math.max(1, Math.round(this.refs.track.clientWidth / first.getBoundingClientRect().width));
  }

  get #atStart() {
    return Math.abs(this.refs.track.scrollLeft) <= 2;
  }

  get #atEnd() {
    const { track } = this.refs;
    return Math.abs(track.scrollLeft) + track.clientWidth >= track.scrollWidth - 2;
  }

  /** @param {1 | -1} direction */
  #step(direction) {
    const loop = this.hasAttribute('data-loop');
    const lastIndex = this.refs.slides.length - 1;

    if (direction === 1 && this.#atEnd) {
      if (loop) this.#scrollToSlide(0);
      return;
    }

    if (direction === -1 && this.#atStart) {
      if (loop) this.#scrollToSlide(lastIndex);
      return;
    }

    const target = this.#currentIndex + direction * this.#perPage;
    this.#scrollToSlide(Math.min(Math.max(target, 0), lastIndex));
  }

  /** @param {number} index */
  #scrollToSlide(index) {
    const { track, slides } = this.refs;
    const slide = slides[index];
    if (!slide) return;

    const offset = slide.getBoundingClientRect().left - track.getBoundingClientRect().left;
    track.scrollTo({ left: track.scrollLeft + offset, behavior: this.#behavior });
  }

  /* Directo en cada scroll (pocos slides, solo lecturas de layout): un throttle con
     requestAnimationFrame se quedaba trabado en pestañas ocultas */
  #handleScroll = () => {
    this.#updateControls();
  };

  #updateControls() {
    const { previous, next, dots, track } = this.refs;
    const loop = this.hasAttribute('data-loop');
    const scrollable = track.scrollWidth > track.clientWidth + 2;

    this.toggleAttribute('data-scrollable', scrollable);
    if (previous) previous.disabled = !scrollable || (!loop && this.#atStart);
    if (next) next.disabled = !scrollable || (!loop && this.#atEnd);

    if (dots?.length) {
      const current = this.#currentIndex;
      dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === current)));
    }
  }

  get #seconds() {
    return Number(this.dataset.autoplay) || 0;
  }

  #restartAutoplay = () => {
    this.#stopAutoplay();
    if (this.#paused || this.#seconds === 0 || this.#reducedMotion.matches) return;

    this.#timer = window.setInterval(() => this.#step(1), this.#seconds * 1000);
  };

  #stopAutoplay() {
    if (this.#timer === null) return;
    clearInterval(this.#timer);
    this.#timer = null;
  }

  #pause = () => {
    this.#paused = true;
    this.#stopAutoplay();
  };

  #resume = () => {
    if (this.contains(document.activeElement)) return;
    this.#paused = false;
    this.#restartAutoplay();
  };

  /** @param {FocusEvent} event */
  #handleFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#resume();
  };
}

if (!customElements.get('elife-slider')) {
  customElements.define('elife-slider', ElifeSlider);
}
